/**
 * "Starter" badge and Keep action (tracker item 76). A record created by an
 * industry starter pack carries a Starter badge on its catalog list until the
 * ADMIN presses Keep (which deletes the flag only; the record is untouched).
 *
 * The flags come from GET /starter-templates/seeded, which is ADMIN only: for
 * every other role the hook makes no request and nothing is shown. A failed
 * load shows no badges (they are a convenience, not a gate).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createAPIClient, IStarterTemplatesApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { canManageStarterTemplates } from '../utils/permissions';
import { getErrorMessage } from '../utils/apiError';
import { rowActionClass } from './formStyles';

export interface StarterRecords {
  /** The record is flagged "Starter" */
  isStarter(entityId: string): boolean;
  /** Press Keep: drop the flag. Resolves true when it worked. */
  keep(entityId: string): Promise<boolean>;
  /** Entity id whose Keep is running */
  busyId: string | null;
  /** A failed Keep: the message and the record it was for (StarterMark shows it) */
  error: { id: string; message: string } | null;
  clearError(): void;
}

/** Flags of one record type. Ids of other types are ignored. */
export function useStarterRecords(entityType: types.StarterEntityType): StarterRecords {
  const { user } = useAuth();
  const allowed = canManageStarterTemplates(user);
  const api = useMemo<IStarterTemplatesApi>(() => createAPIClient(), []);
  // entityId -> starter_seeded_records.id
  const [flags, setFlags] = useState<Map<string, string>>(() => new Map());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<{ id: string; message: string } | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await api.listSeededStarterRecords();
        if (cancelled) return;
        const next = new Map<string, string>();
        for (const pack of result.packs) {
          for (const record of pack.records) if (record.entityType === entityType) next.set(record.entityId, record.id);
        }
        setFlags(next);
      } catch {
        if (!cancelled) setFlags(new Map());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, api, entityType]);

  const keep = useCallback(
    async (entityId: string) => {
      const seededId = flags.get(entityId);
      if (!seededId) return false;
      setBusyId(entityId);
      setError(null);
      try {
        await api.keepStarterRecord(seededId);
        setFlags((current) => {
          const next = new Map(current);
          next.delete(entityId);
          return next;
        });
        return true;
      } catch (err) {
        setError({ id: entityId, message: getErrorMessage(err, 'The Starter label could not be removed.') });
        return false;
      } finally {
        setBusyId(null);
      }
    },
    [api, flags]
  );

  return useMemo(
    () => ({ isStarter: (id: string) => allowed && flags.has(id), keep, busyId, error, clearError: () => setError(null) }),
    [allowed, flags, keep, busyId, error]
  );
}

export function StarterBadge() {
  return (
    <span
      className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-800 ring-1 ring-inset ring-indigo-200"
      title="Added by an industry starter pack. Press Keep once you have reviewed it."
    >
      Starter
    </span>
  );
}

/** The badge with its Keep button, or nothing when the record is not flagged. `name` labels the button for screen readers. */
export function StarterMark({ records, entityId, name }: { records: StarterRecords; entityId: string; name: string }) {
  if (!records.isStarter(entityId)) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-0">
      <StarterBadge />
      <button
        type="button"
        className={`${rowActionClass} min-h-[44px] md:min-h-0`}
        disabled={records.busyId !== null}
        aria-label={`Keep ${name}: remove its Starter label`}
        onClick={() => void records.keep(entityId)}
      >
        {records.busyId === entityId ? 'Saving...' : 'Keep'}
      </button>
      {records.error?.id === entityId && <span role="alert" className="w-full text-xs text-red-700">{records.error.message}</span>}
    </span>
  );
}
